import { debug } from '../lib/debug';
import type { ApiFetchOptions } from '../types';

const TOKEN_STORAGE_KEY = 'dispatchAuthToken';

// 401発生時のコールバック（AuthContextから設定される）
let onUnauthorizedCallback: (() => void) | null = null;

export const setOnUnauthorized = (cb: (() => void) | null): void => {
  onUnauthorizedCallback = cb;
};

const sanitizeBaseUrl = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  let normalized = trimmed.replace(/\"/g, '');
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(normalized);
  const isProtocolRelative = normalized.startsWith('//');

  if (isAbsolute) {
    return normalized.replace(/\/+$/, '');
  }

  if (isProtocolRelative) {
    return `https:${normalized}`.replace(/\/+$/, '');
  }

  const domainLikePattern = /^[a-z0-9.-]+(:\d+)?(\/.*)?$/i;
  const hasDomainHint = /[.:]/.test(normalized);
  if (hasDomainHint && domainLikePattern.test(normalized) && !normalized.startsWith('/')) {
    const prefersHttp = /^localhost/i.test(normalized) || /^127\./.test(normalized);
    const protocol = prefersHttp ? 'http' : 'https';
    return `${protocol}://${normalized}`.replace(/\/+$/, '');
  }

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }

  return normalized.replace(/\/+$/, '');
};

const resolveBaseCandidates = (): string => {
  const candidates: string[] = [];

  const envCandidates = [
    process.env.REACT_APP_DISPATCH_API_URL,
    process.env.REACT_APP_API_URL,
  ];

  envCandidates.forEach((value) => {
    const sanitized = sanitizeBaseUrl(value);
    if (sanitized) {
      candidates.push(sanitized);
    }
  });

  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin)) {
      const normalizedOrigin = origin.replace(/\/+$/, '');
      candidates.push(`${normalizedOrigin}/api`);
      candidates.push('/dispatch-api');
    }
  }

  candidates.push('/dispatch-api');
  candidates.push('http://localhost:7070/api');

  const resolved = candidates.filter((candidate) => typeof candidate === 'string' && candidate.trim() !== '');

  return resolved[0] || '/dispatch-api';
};

const DISPATCH_API_BASE_URL: string = resolveBaseCandidates();

if (typeof window !== 'undefined') {
  try {
    // Expose for quick sanity checks in the console when debugging production bundles.
    (window as any).__DISPATCH_API_BASE_URL__ = DISPATCH_API_BASE_URL;
  } catch (error) {
    debug.warn('dispatchClient', 'failed to expose base url on window', error);
  }
}

try {
  // console.info('[dispatchClient] base URL resolved to', DISPATCH_API_BASE_URL);
} catch (_) {
  // Ignore logging failures in environments without console.
}

let authToken: string | null = null;

const isBrowser: boolean = typeof window !== 'undefined';

const loadTokenFromStorage = (): string | null => {
  if (!isBrowser) return null;
  try {
    const stored = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored && typeof stored === 'string') {
      return stored;
    }
  } catch (error) {
    debug.warn('dispatchClient', 'failed to read token from storage', error);
  }
  return null;
};

const saveTokenToStorage = (token: string | null): void => {
  if (!isBrowser) return;
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    debug.warn('dispatchClient', 'failed to persist token', error);
  }
};

const getCurrentToken = (): string | null => {
  if (authToken) return authToken;

  // まずAuthContextのトークンを確認
  const userToken = isBrowser ? window.localStorage.getItem('authToken') : null;
  if (userToken) {
    authToken = userToken;
    return authToken;
  }

  // フォールバック: dispatchAuthTokenを確認
  const stored = loadTokenFromStorage();
  if (stored) {
    authToken = stored;
    return authToken;
  }
  return null;
};

// 外部からトークンを設定する関数（AuthContextから呼び出される）
export const setAuthToken = (token: string | null): void => {
  authToken = token;
  if (token) {
    saveTokenToStorage(token);
  } else {
    clearAuthToken();
  }
};


const ensureAuthToken = async ({ forceRefresh = false } = {}): Promise<string> => {
  if (!forceRefresh) {
    const existing = getCurrentToken();
    if (existing) {
      return existing;
    }
  }

  // トークンがない場合はエラー
  // ユーザーログインが必要
  throw new Error('認証トークンがありません。ログインしてください。');
};

const buildUrl = (path: string): string => {
  if (typeof path !== 'string' || !path.trim()) {
    return DISPATCH_API_BASE_URL;
  }
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const trimmed = path.replace(/^\/+/, '');
  return `${DISPATCH_API_BASE_URL}/${trimmed}`;
};

export const apiFetch = async (path: string, options: ApiFetchOptions = {}): Promise<Response> => {
  const {
    headers = {},
    retryOnUnauthorized = true,
    skipAuth = false,
    ...rest
  } = options;

  const requestInit: any = { ...rest, headers: { ...(headers as Record<string, string>) } };
  if (!skipAuth) {
    const token = await ensureAuthToken();
    requestInit.headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...requestInit.headers,
    };
    if (requestInit.body && !(requestInit.body instanceof FormData)) {
      requestInit.headers['Content-Type'] = requestInit.headers['Content-Type'] || 'application/json';
    }
  } else if (!requestInit.headers.Accept) {
    requestInit.headers.Accept = 'application/json';
  }

  const response = await fetch(buildUrl(path), requestInit);

  if (response.status === 401 && !skipAuth) {
    debug.warn('dispatchClient', 'received 401, triggering soft logout');

    // dispatchClient内部のトークンのみクリア（localStorageは触らない）
    // localStorage操作はAuthContextのlogout()に一任し、
    // storageイベントによるタブ間同期ループを防止する
    authToken = null;

    // AuthContextのソフトログアウトをトリガー
    if (onUnauthorizedCallback) {
      onUnauthorizedCallback();
    }

    const error: any = new Error('認証が必要です。再度ログインしてください。');
    error.status = 401;
    throw error;
  }

  return response;
};

export const apiFetchJson = async (path: string, options: ApiFetchOptions = {}): Promise<any> => {
  const response = await apiFetch(path, options);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let errorMessage = `APIリクエストに失敗しました: ${response.status}`;

    try {
      const json = JSON.parse(text);
      if (json.message) {
        errorMessage = json.message;
      }
      if (json.errors) {
        const details = Object.values(json.errors).flat().join(', ');
        if (details) {
          errorMessage += ` (${details})`;
        }
      }
    } catch (e) {
      // ignore json parse error
    }

    const snippet = typeof text === 'string' ? text.slice(0, 300) : '';
    const error: any = new Error(errorMessage);
    error.status = response.status;
    error.body = snippet;
    error.url = response.url || path;
    debug.error('dispatchClient', 'request failed', { url: error.url, status: response.status, snippet });
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  const rawText = await response.text().catch(() => '');
  if (!rawText) {
    return null;
  }

  try {
    return JSON.parse(rawText);
  } catch (cause) {
    const snippet = typeof rawText === 'string' ? rawText.slice(0, 300) : '';
    const error: any = new Error(`APIレスポンスの解析に失敗しました: ${response.url || path}`);
    error.status = response.status;
    error.body = snippet;
    error.url = response.url || path;
    error.cause = cause;
    debug.error('dispatchClient', 'failed to parse JSON response', { url: error.url, status: response.status, snippet });
    throw error;
  }
};

export const clearAuthToken = (): void => {
  authToken = null;
  saveTokenToStorage(null);
};

export const getDispatchApiBaseUrl = (): string => DISPATCH_API_BASE_URL;
